import React from 'react';
import { Calendar, ArrowLeft, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SeasonalProduceWidget from '../widgets/SeasonalProduceWidget';
import RecipeSuggestionsWidget from '../widgets/RecipeSuggestionsWidget';
import FarmRecommendationsWidget from '../widgets/FarmRecommendationsWidget';
import RelatedContentWidget from '../widgets/RelatedContentWidget';

interface SeasonalGuide {
  id: string;
  title?: string;
  name?: string;
  month?: number;
  description?: string;
  featuredProduce?: string[];
  recipes?: string[];
  tips?: string[];
}

interface RelatedItem {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  url: string;
  type: 'recipe' | 'blog' | 'produce' | 'farm' | 'seasonal';
  image?: string;
  meta?: string;
}

interface SeasonalGuideSidebarProps {
  guide: SeasonalGuide;
  relatedGuides?: RelatedItem[];
  showFeaturedProduce?: boolean;
  showRecipes?: boolean;
  showFarms?: boolean;
}

const SeasonalGuideSidebar: React.FC<SeasonalGuideSidebarProps> = ({
  guide,
  relatedGuides = [],
  showFeaturedProduce = true,
  showRecipes = true,
  showFarms = true,
}) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const getMonthUrl = (monthNum: number): string => {
    const monthName = monthNames[monthNum - 1].toLowerCase();
    return `/seasonal/${monthName}`;
  };

  const getPreviousMonth = (): number => {
    if (!guide.month) return 12;
    return guide.month === 1 ? 12 : guide.month - 1;
  };

  const getNextMonth = (): number => {
    if (!guide.month) return 1;
    return guide.month === 12 ? 1 : guide.month + 1;
  };

  const currentMonthName = guide.month ? monthNames[guide.month - 1] : 'Current';
  const prevMonth = getPreviousMonth();
  const nextMonth = getNextMonth();

  const styles = {
    sidebar: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    monthNavWidget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    monthNavTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
    },
    monthNavIcon: {
      marginRight: '12px',
      color: '#2D5A27',
    },
    currentMonth: {
      textAlign: 'center' as const,
      padding: '16px',
      backgroundColor: '#2D5A27',
      color: '#fff',
      borderRadius: '8px',
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px',
    },
    navButtons: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
    },
    navButton: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      padding: '12px',
      backgroundColor: '#f9f9f9',
      border: '2px solid #e5e5e5',
      borderRadius: '6px',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    } as React.CSSProperties,
    navButtonHover: {
      backgroundColor: '#f0f7ef',
      borderColor: '#2D5A27',
      transform: 'translateY(-2px)',
    },
    navButtonLabel: {
      fontSize: '11px',
      color: '#666',
      textTransform: 'uppercase' as const,
      fontWeight: '600',
      marginBottom: '6px',
    },
    navButtonMonth: {
      fontSize: '14px',
      color: '#1a1a1a',
      fontWeight: '600',
    },
    monthGridWidget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    monthGridTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '16px',
    },
    monthGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '8px',
    },
    monthCell: {
      padding: '10px 8px',
      textAlign: 'center' as const,
      backgroundColor: '#f9f9f9',
      border: '1px solid #e5e5e5',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      color: '#666',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    } as React.CSSProperties,
    monthCellActive: {
      backgroundColor: '#2D5A27',
      borderColor: '#2D5A27',
      color: '#fff',
    },
    monthCellHover: {
      backgroundColor: '#f0f7ef',
      borderColor: '#2D5A27',
      color: '#2D5A27',
    },
    tipsWidget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    tipsTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '16px',
    },
    tipsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    tipItem: {
      fontSize: '14px',
      color: '#333',
      lineHeight: '1.6',
      paddingLeft: '20px',
      position: 'relative' as const,
      marginBottom: '12px',
    },
    tipBullet: {
      position: 'absolute' as const,
      left: '0',
      top: '0',
      color: '#2D5A27',
      fontWeight: '600',
    },
  };

  return (
    <div style={styles.sidebar}>
      {/* Month Navigator */}
      <div style={styles.monthNavWidget}>
        <h3 style={styles.monthNavTitle}>
          <Calendar style={styles.monthNavIcon} size={24} />
          Navigate by Month
        </h3>

        <div style={styles.currentMonth}>
          {currentMonthName}
          {guide.month && ` (Month ${guide.month})`}
        </div>

        <div style={styles.navButtons}>
          <Link
            to={getMonthUrl(prevMonth)}
            style={styles.navButton}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, styles.navButtonHover);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f9f9f9';
              e.currentTarget.style.borderColor = '#e5e5e5';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <ArrowLeft size={20} style={{ color: '#2D5A27', marginBottom: '6px' }} />
            <div style={styles.navButtonLabel}>Previous</div>
            <div style={styles.navButtonMonth}>{monthNames[prevMonth - 1]}</div>
          </Link>

          <Link
            to={getMonthUrl(nextMonth)}
            style={styles.navButton}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, styles.navButtonHover);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f9f9f9';
              e.currentTarget.style.borderColor = '#e5e5e5';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <ArrowRight size={20} style={{ color: '#2D5A27', marginBottom: '6px' }} />
            <div style={styles.navButtonLabel}>Next</div>
            <div style={styles.navButtonMonth}>{monthNames[nextMonth - 1]}</div>
          </Link>
        </div>
      </div>

      {/* All Months Grid */}
      <div style={styles.monthGridWidget}>
        <h4 style={styles.monthGridTitle}>All Seasonal Guides</h4>
        <div style={styles.monthGrid}>
          {monthNames.map((month, index) => {
            const monthNum = index + 1;
            const isActive = monthNum === guide.month;
            return (
              <Link
                key={month}
                to={getMonthUrl(monthNum)}
                style={{
                  ...styles.monthCell,
                  ...(isActive ? styles.monthCellActive : {}),
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    Object.assign(e.currentTarget.style, styles.monthCellHover);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = '#f9f9f9';
                    e.currentTarget.style.borderColor = '#e5e5e5';
                    e.currentTarget.style.color = '#666';
                  }
                }}
              >
                {month}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Featured Produce */}
      {showFeaturedProduce && (
        <SeasonalProduceWidget
          maxItems={6}
        />
      )}

      {/* Seasonal Recipes */}
      {showRecipes && guide.featuredProduce && guide.featuredProduce.length > 0 && (
        <RecipeSuggestionsWidget
          ingredients={guide.featuredProduce}
          maxRecipes={5}
        />
      )}

      {/* Farm Recommendations */}
      {showFarms && (
        <FarmRecommendationsWidget
          allFarms={[]}
          maxRecommendations={4}
        />
      )}

      {/* Shopping Tips */}
      {guide.tips && guide.tips.length > 0 && (
        <div style={styles.tipsWidget}>
          <h4 style={styles.tipsTitle}>Shopping Tips</h4>
          <ul style={styles.tipsList}>
            {guide.tips.slice(0, 5).map((tip, index) => (
              <li key={index} style={styles.tipItem}>
                <span style={styles.tipBullet}>💡</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Related Guides */}
      {relatedGuides && relatedGuides.length > 0 && (
        <RelatedContentWidget
          items={relatedGuides}
          title="Related Guides"
          contentType="guide"
          maxItems={4}
          layout="list"
        />
      )}
    </div>
  );
};

export default SeasonalGuideSidebar;
